import { AxiosError } from "axios";

// 4xx statuses worth reporting despite being client-class (timeout / rate limit).
const REPORTABLE_CLIENT_STATUSES = new Set([408, 429]);

// A no-response, transient network drop (the server never answered): a brief
// connectivity blip that should be retried and, on its own, must NOT reach the
// monitor as noise. If the server DID answer (5xx/4xx) this is false — that is a
// real server signal, not a client-side network blip. Shared by the retry policy
// (QueryClient) and the report filter below so there is a single definition.
export const isTransientNetworkError = (error: unknown): boolean => {
  const e = error as AxiosError | null | undefined;
  if (!e || e.response) return false;
  return (
    e.code === "ERR_NETWORK" ||
    e.code === "ECONNABORTED" ||
    e.message === "Network Error"
  );
};

// Pure decision: is this axios failure worth sending to the error monitor?
// Reports no-response network/timeout failures and 5xx/408/429. Skips request
// cancellations (tab-switch/query-inactive aborts), routine auth (401/403), and
// the monitor endpoint's own failures (infinite-loop guard).
export const shouldReportApiError = (error: AxiosError): boolean => {
  if (error.code === "ERR_CANCELED") return false;

  const url = error.config?.url ?? "";
  if (url.includes("/monitor/report")) return false;

  if (!error.response) {
    return isTransientNetworkError(error);
  }

  const status = error.response.status;
  return status >= 500 || REPORTABLE_CLIENT_STATUSES.has(status);
};

export interface ApiErrorReport {
  name: string;
  message: string;
  context: Record<string, string | number | boolean | null>;
}

// Pure builder: shape the monitor payload. `name` prefix groups API errors into
// their own fingerprint family (ApiError:<status|code>), separate from JS
// exceptions. Time and socket state are injected by the caller (no hidden globals).
export const buildApiErrorReport = (
  error: AxiosError,
  socket: { connected: boolean; id: string | null },
  durationMs: number | null
): ApiErrorReport => {
  const status = error.response?.status ?? 0;
  const code = error.code ?? "unknown";
  const method = (error.config?.method ?? "get").toUpperCase();
  const url = error.config?.url ?? "unknown";
  const label = status > 0 ? String(status) : code;

  return {
    name: `ApiError:${label}`,
    message: `${method} ${url} -> ${label}`,
    context: {
      url,
      method,
      status,
      code,
      durationMs,
      socketConnected: socket.connected,
      socketId: socket.id
    }
  };
};
